import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import Table from 'rsuite/Table'
import Tooltip from 'rsuite/Tooltip'
import Whisper from 'rsuite/Whisper'
import Pagination from 'rsuite/Pagination'
import IconButton from 'rsuite/esm/IconButton'
import {FiEye} from 'react-icons/fi'
import {SortType} from 'rsuite-table/lib/@types/common'
import Select from 'react-select'
import {StateParamsModel} from 'src/app/modules/common/Model'
import DesignComponent from 'src/app/modules/common/components/designComponent'
import RSuiteTable from 'src/cms/helpers/components/rsuiteTable'
import * as auditTrailRedux from '../index'
import {LedgerModel} from '../Model/Model'
import ViewLedgerDiff from './ViewDiff'

const EVENT_OPTIONS = [
  {label: 'All', value: ''},
  {label: 'Created', value: 'created'},
  {label: 'Updated', value: 'updated'},
  {label: 'Deleted', value: 'deleted'},
  {label: 'Restored', value: 'restored'},
]

const selectMenuStyles = {
  menu: (base: any) => ({...base, zIndex: 9999}),
  menuPortal: (base: any) => ({...base, zIndex: 9999}),
}

const AuditTrailPage = () => {
  const dispatch = useDispatch()
  const {data, filterOptions, loading} = useSelector((state: any) => state.auditTrail)
  const [params, setParams] = useState<
    StateParamsModel & {event?: string; userId?: string; recordableType?: string}
  >({page: 1, limit: 10})
  const [selectedEntry, setSelectedEntry] = useState<LedgerModel | null>(null)

  const Cell = Table.Cell

  const ActionCell = ({rowData, ...props}: any) => (
    <Cell {...props} className='link-group'>
      <Whisper placement='top' trigger='hover' speaker={<Tooltip>View Diff</Tooltip>}>
        <IconButton
          appearance='subtle'
          icon={<FiEye />}
          onClick={() => setSelectedEntry(rowData)}
        />
      </Whisper>
    </Cell>
  )

  const columns = [
    {label: 'S.N.', dataKey: 'sn', width: 60, cell: <Cell dataKey='sn' />},
    {label: 'Actor', dataKey: 'actor', width: 160, cell: <Cell dataKey='actor' />},
    {label: 'Model', dataKey: 'model', width: 160, cell: <Cell dataKey='model' />},
    {label: 'Event', dataKey: 'event', width: 120, cell: <Cell dataKey='event' />},
    {
      label: 'Changed Fields',
      dataKey: 'changedFields',
      flexGrow: 1,
      cell: <Cell>{(rowData: LedgerModel) => rowData.changedFields.join(', ') || '-'}</Cell>,
    },
    {label: 'Date', dataKey: 'createdAt', width: 200, cell: <Cell dataKey='createdAt' />},
    {label: 'Diff', width: 80, align: 'center', cell: <ActionCell dataKey='id' />},
  ]

  useEffect(() => {
    dispatch(auditTrailRedux.actions.getAuditTrail(params))
  }, [params])

  useEffect(() => {
    dispatch(auditTrailRedux.actions.getAuditTrailFilterOptions())
  }, [])

  const USER_OPTIONS = [
    {label: 'All', value: ''},
    ...(filterOptions?.actors?.map((actor: {id: string; name: string}) => ({
      label: actor.name,
      value: actor.id,
    })) ?? []),
  ]

  const MODEL_OPTIONS = [
    {label: 'All', value: ''},
    ...(filterOptions?.models?.map((model: {value: string; label: string}) => ({
      label: model.label,
      value: model.value,
    })) ?? []),
  ]

  const tableData =
    data?.ledger?.map((entry: LedgerModel, i: number) => ({
      ...entry,
      sn: (params?.page - 1) * params?.limit + (i + 1),
    })) ?? []

  const handleSort = (dataKey: string, sortType?: SortType) => {
    setParams({...params, orderBy: dataKey, order: sortType})
  }

  const handleRefresh = () => {
    dispatch(auditTrailRedux.actions.getAuditTrail(params))
  }

  return (
    <>
      <div className='shadow p-3 bg-white rounded'>
        <div className='d-flex flex-wrap mb-3'>
          <div className='col-md-3 me-3 position-relative' style={{zIndex: 10}}>
            <Select
              id='userFilter'
              className='basic-single'
              isClearable={true}
              menuPlacement='bottom'
              menuPortalTarget={document.body}
              styles={selectMenuStyles}
              options={USER_OPTIONS}
              onChange={(option: any) => {
                setParams({...params, userId: option?.value, page: 1})
              }}
              value={
                USER_OPTIONS.find((o) => o.value === String(params.userId ?? '')) ||
                USER_OPTIONS[0]
              }
              placeholder='Filter by User'
            />
          </div>
          <div className='col-md-3 position-relative' style={{zIndex: 10}}>
            <Select
              id='modelFilter'
              className='basic-single'
              isClearable={true}
              menuPlacement='bottom'
              menuPortalTarget={document.body}
              styles={selectMenuStyles}
              options={MODEL_OPTIONS}
              onChange={(option: any) => {
                setParams({...params, recordableType: option?.value, page: 1})
              }}
              value={
                MODEL_OPTIONS.find((o) => o.value === String(params.recordableType ?? '')) ||
                MODEL_OPTIONS[0]
              }
              placeholder='Filter by Model'
            />
          </div>
        </div>

        <DesignComponent
          moduleName='Audit Trail'
          params={params}
          setParams={setParams}
          handleRefresh={handleRefresh}
          exportShow={false}
          importShow={false}
          addShow={false}
          deleteShow={false}
          toggleMultipleShow={false}
          filterData
          enableDropdownFilter
          dropdownOption={EVENT_OPTIONS}
          handleDropdownChange={(option: any) => {
            setParams({...params, event: option?.value, page: 1})
          }}
          dropdownValue={
            EVENT_OPTIONS.find((o) => o.value === String(params.event ?? '')) || EVENT_OPTIONS[0]
          }
          dropdownPlaceholder='Filter by Event'
          loading={loading}
        />

        <div className='datatable'>
          <RSuiteTable
            showCheckbox={false}
            columns={columns}
            data={tableData}
            showLoading={loading}
            handleSort={handleSort}
          />
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            className='mt-5'
            maxButtons={5}
            size='sm'
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={data?.meta?.total || 0}
            limitOptions={[10, 20, 50, 100]}
            limit={params?.limit}
            activePage={params?.page}
            onChangePage={(value) => setParams({...params, page: value})}
            onChangeLimit={(value) => setParams({...params, limit: value})}
          />
        </div>
      </div>

      {selectedEntry && (
        <ViewLedgerDiff entry={selectedEntry} handleClose={() => setSelectedEntry(null)} />
      )}
    </>
  )
}

export default AuditTrailPage
